import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { cn } from '@verlo/shared/lib/utils';
import { Button } from '@verlo/shared/components/ui/button';
import { Notice } from '@verlo/shared/components/ui/notice';
import { reportsIntelApi } from '@verlo/shared/services/api';
import { Loader2, RefreshCw } from 'lucide-react';

/**
 * Verlo Insight — the one piece behind every plain-English read in the back office
 * (16 Sep 2026). It has four states and no others:
 *
 *   off        Insight is not on for this org (no key, or switched off in Settings) — the
 *              piece renders nothing at all. No badge, no "preview", no button that does nothing.
 *   idle       the eyebrow, a line saying what it will read, and Generate.
 *   generated  the text in reading type, "Generated · 2 min ago", Refresh.
 *   couldn't   a Notice in the stop tone with one sentence and Try again.
 *
 * `read` is an async function returning the route's response: `{available, text|body|insight,
 * reason}`. The piece normalises the three field names the routes grew up with; a `render(data)`
 * prop draws a structured result (the daily priority's three items) in place of the paragraph.
 *
 *   <Insight read={() => reportsIntelApi.pnlInsight({ year, month })}
 *            purpose="What moved the bottom line in Sep 2026, and one check to run this week." />
 */

const InsightContext = createContext({ status: null, loaded: false, refresh: () => {} });

/** Reads `GET /reports/insight/status` once per session. Mount inside the back office. */
export const InsightProvider = ({ children }) => {
  const [status, setStatus] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const refresh = useCallback(() => reportsIntelApi.insightStatus()
    .then(({ data }) => { setStatus(data); setLoaded(true); })
    .catch(() => { setStatus({ enabled: false, configured: false }); setLoaded(true); }), []);
  useEffect(() => { refresh(); }, [refresh]);
  const value = useMemo(() => ({ status, loaded, enabled: !!status?.enabled, refresh }), [status, loaded, refresh]);
  return <InsightContext.Provider value={value}>{children}</InsightContext.Provider>;
};

export const useInsight = () => useContext(InsightContext);

const REASONS = {
  cap_reached: "Today's limit for Verlo Insight has been reached. It resets at midnight.",
  rate_limited: 'Verlo Insight is busy. Try again in a moment.',
  disabled: 'Verlo Insight is switched off for this business.',
  not_configured: 'Verlo Insight is not set up.',
  unavailable: "Couldn't generate a read. Try again.",
};

const textOf = (data) => data?.text ?? data?.body ?? data?.insight ?? null;

const ago = (iso) => {
  if (!iso) return null;
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  return `${Math.round(s / 3600)} h ago`;
};

const Insight = ({ read, purpose, headline, className, compact = false, testId = 'insight', onResult, render, generateLabel = 'Generate' }) => {
  const { enabled } = useInsight();
  const [state, setState] = useState({ phase: 'idle', data: null, reason: null });

  const generate = useCallback(async () => {
    setState((s) => ({ ...s, phase: 'loading' }));
    try {
      const res = await read();
      const data = res?.data ?? res;
      onResult?.(data);
      if (data?.available === false || (!render && !textOf(data))) {
        setState({ phase: 'couldnt', data: null, reason: data?.reason || 'unavailable' });
      } else {
        setState({ phase: 'generated', data, reason: null });
      }
    } catch {
      setState({ phase: 'couldnt', data: null, reason: 'unavailable' });
    }
  }, [read, onResult]);

  if (!enabled) return null;

  const { phase, data, reason } = state;
  const loading = phase === 'loading';

  return (
    <section className={cn('rounded-lg border border-hairline bg-white', compact ? 'px-3 py-2.5' : 'px-4 py-3', className)} data-testid={testId} data-phase={phase}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-heading text-[11px] font-bold uppercase tracking-[0.06em] text-ink-faint">Verlo Insight</div>
          {headline && phase !== 'generated' && <div className="mt-0.5 truncate text-[13.5px] font-semibold text-ink">{headline}</div>}
        </div>
        {(phase === 'idle' || phase === 'generated' || loading) && (
          <Button size="sm" variant={phase === 'generated' ? 'outline' : 'default'} className={cn('h-8 shrink-0', phase !== 'generated' && 'bg-verlo hover:bg-verlo-hover')} onClick={generate} disabled={loading} data-testid={`${testId}-generate`}>
            {loading ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : phase === 'generated' ? <RefreshCw size={13} className="mr-1.5" /> : null}
            {loading ? 'Reading…' : phase === 'generated' ? 'Refresh' : generateLabel}
          </Button>
        )}
      </div>

      {phase === 'idle' && purpose && <p className="mt-2 text-[13px] leading-relaxed text-ink-soft">{purpose}</p>}
      {loading && !data && <p className="mt-2 text-[13px] text-ink-soft">Reading the numbers…</p>}
      {phase === 'generated' && (
        <div className="mt-2" data-testid={`${testId}-text`}>
          {render ? render(data) : (
            <>
              {data.headline && !headline && <p className="mb-1 text-[13.5px] font-semibold text-ink">{data.headline}</p>}
              <p className="whitespace-pre-wrap text-[13.5px] leading-relaxed text-ink">{textOf(data)}</p>
            </>
          )}
          <p className="mt-2 text-[12px] text-ink-faint">Generated · {ago(data.generated_at) || 'just now'}</p>
        </div>
      )}
      {phase === 'couldnt' && (
        <div className="mt-2">
          <Notice tone="stop" word="Insight" title={REASONS[reason] || REASONS.unavailable} className="mb-0" data-testid={`${testId}-couldnt`} />
          {reason !== 'cap_reached' && reason !== 'disabled' && reason !== 'not_configured' && (
            <Button size="sm" variant="outline" className="mt-2 h-8" onClick={generate} data-testid={`${testId}-retry`}>Try again</Button>
          )}
        </div>
      )}
    </section>
  );
};

export default Insight;
